import { buildAIPrompt, SYMPTOM_NAMES } from './aiPromptTemplate';

/**
 * Build context summary from female hormone balance survey answers
 */
export function buildFemaleContext(answers) {
    const contextParts = [];

    // Safety check
    if (answers.safety) {
        if (answers.safety.includes('none')) {
            contextParts.push('- Good health condition, no specific risks');
        } else {
            contextParts.push('- Some health concerns to note');
        }
    }

    // Demographics
    if (answers.demographics) {
        const { age, weight } = answers.demographics;
        if (age) contextParts.push(`- Age group: ${age}`);
        if (weight) contextParts.push(`- Weight: ${weight}`);
    }

    // Main concern
    if (answers.mainConcern) {
        const concernMap = {
            'menstrual': 'Irregular menstruation',
            'mood': 'Mood issues',
            'menopause': 'Menopause symptoms',
            'fertility': 'Fertility conditioning',
            'overall': 'Overall conditioning'
        };
        contextParts.push(`- Main concern: ${concernMap[answers.mainConcern]}`);
    }

    // 分支A：月经不调
    if (answers.menstrualIssues && answers.menstrualIssues.length > 0) {
        const issues = answers.menstrualIssues.map(issue => {
            switch (issue) {
                case 'irregular_cycle': return 'Irregular cycle';
                case 'light_flow': return 'Light flow';
                case 'heavy_flow': return 'Heavy flow';
                case 'dysmenorrhea': return 'Dysmenorrhea';
                default: return issue;
            }
        });
        contextParts.push(`- Menstrual issues: ${issues.join(', ')}`);
    }

    if (answers.menstrualCycle) {
        const cycleMap = {
            'early': 'Cycle early (<25 days)',
            'late': 'Cycle late (>35 days)',
            'irregular': 'Irregular cycle',
            'normal_cycle': 'Normal cycle'
        };
        contextParts.push(`- ${cycleMap[answers.menstrualCycle]}`);
    }

    if (answers.dysmenorrheaSeverity) {
        const severityMap = {
            'mild': 'Mild dysmenorrhea',
            'moderate': 'Moderate dysmenorrhea',
            'severe': 'Severe dysmenorrhea'
        };
        contextParts.push(`- ${severityMap[answers.dysmenorrheaSeverity]}`);
    }

    if (answers.dysmenorrheaSymptoms && answers.dysmenorrheaSymptoms.length > 0) {
        const symptoms = answers.dysmenorrheaSymptoms.map(s => {
            switch (s) {
                case 'blood_clots': return 'Many blood clots';
                case 'back_pain': return 'Back pain';
                case 'cold_limbs': return 'Cold hands and feet';
                case 'nausea': return 'Nausea';
                default: return s;
            }
        });
        contextParts.push(`- Dysmenorrhea symptoms: ${symptoms.join(', ')}`);
    }

    // 分支B：情绪问题
    if (answers.moodIssues && answers.moodIssues.length > 0) {
        const issues = answers.moodIssues.map(issue => {
            switch (issue) {
                case 'irritable': return 'Irritable';
                case 'anxious': return 'Anxious';
                case 'depressed': return 'Depressed';
                case 'emotional': return 'Emotional swings';
                default: return issue;
            }
        });
        contextParts.push(`- Mood issues: ${issues.join(', ')}`);
    }

    if (answers.moodTiming) {
        const timingMap = {
            'before_period': 'Worse before period',
            'during_period': 'Worse during period',
            'all_month': 'Mood issues all month',
            'stress': 'Worse with stress'
        };
        contextParts.push(`- ${timingMap[answers.moodTiming]}`);
    }

    if (answers.moodDuration) {
        const durationMap = {
            '1-3months': 'Mood issues for 1-3 months',
            '3-6months': 'Mood issues for 3-6 months',
            '>6months': 'Mood issues for over 6 months'
        };
        contextParts.push(`- ${durationMap[answers.moodDuration]}`);
    }

    // 分支C：更年期
    if (answers.menopauseSymptoms && answers.menopauseSymptoms.length > 0) {
        const symptoms = answers.menopauseSymptoms.map(s => {
            switch (s) {
                case 'hot_flashes': return 'Hot flashes/sweating';
                case 'insomnia': return 'Insomnia';
                case 'mood': return 'Irritable mood';
                case 'palpitations': return 'Palpitations';
                case 'joint_pain': return 'Joint pain';
                default: return s;
            }
        });
        contextParts.push(`- Menopause symptoms: ${symptoms.join(', ')}`);
    }

    if (answers.menopauseMenstrual) {
        const menstrualMap = {
            'regular': 'Period still regular',
            'irregular': 'Period becoming irregular',
            'stopped': 'Menopause reached'
        };
        contextParts.push(`- ${menstrualMap[answers.menopauseMenstrual]}`);
    }

    if (answers.menopauseDuration) {
        const durationMap = {
            'recent': 'Symptoms started recently',
            '6-12months': 'Symptoms for 6-12 months',
            '>1year': 'Symptoms for over 1 year'
        };
        contextParts.push(`- ${durationMap[answers.menopauseDuration]}`);
    }

    // 分支D：备孕
    if (answers.fertilityDuration) {
        const durationMap = {
            'just_started': 'Just started trying',
            '3-6months': 'Trying for 3-6 months',
            '6-12months': 'Trying for 6-12 months',
            '>1year': 'Trying for over 1 year'
        };
        contextParts.push(`- ${durationMap[answers.fertilityDuration]}`);
    }

    if (answers.fertilityMenstrual) {
        const menstrualMap = {
            'regular': 'Regular cycle',
            'somewhat_irregular': 'Somewhat irregular',
            'very_irregular': 'Very irregular'
        };
        contextParts.push(`- ${menstrualMap[answers.fertilityMenstrual]}`);
    }

    if (answers.fertilityCondition && answers.fertilityCondition.length > 0) {
        const conditions = answers.fertilityCondition
            .filter(c => c !== 'good')
            .map(c => {
                switch (c) {
                    case 'cold_limbs': return 'Cold hands and feet';
                    case 'fatigue': return 'Easily fatigued';
                    case 'poor_sleep': return 'Poor sleep';
                    case 'stress': return 'High stress/anxiety';
                    default: return c;
                }
            });
        if (conditions.length > 0) {
            contextParts.push(`- Fertility status: ${conditions.join(', ')}`);
        }
    }

    // 分支E：整体调理
    if (answers.overallGoals && answers.overallGoals.length > 0) {
        const goals = answers.overallGoals.map(g => {
            switch (g) {
                case 'prevent_menstrual': return 'Prevent menstrual issues';
                case 'skin': return 'Improve skin';
                case 'mood': return 'Improve mood';
                case 'energy': return 'Boost energy';
                default: return g;
            }
        });
        contextParts.push(`- Goals: ${goals.join(', ')}`);
    }

    if (answers.overallMenstrual) {
        const menstrualMap = {
            'very_regular': 'Very regular',
            'occasional': 'Occasional discomfort',
            'some_issues': 'Some minor issues'
        };
        contextParts.push(`- Menstrual status: ${menstrualMap[answers.overallMenstrual]}`);
    }

    // Constitution
    if (answers.constitution && answers.constitution.length > 0) {
        const constitution = answers.constitution
            .filter(c => c !== 'none')
            .map(c => {
                switch (c) {
                    case 'cold_limbs': return 'Cold hands and feet';
                    case 'heat': return 'Prone to inflammation';
                    case 'sweating': return 'Easily sweating';
                    case 'dry_mouth': return 'Dry mouth';
                    default: return c;
                }
            });
        if (constitution.length > 0) {
            contextParts.push(`- Constitution: ${constitution.join(', ')}`);
        }
    }

    // Other symptoms
    if (answers.otherSymptoms && answers.otherSymptoms.length > 0) {
        const symptoms = answers.otherSymptoms
            .filter(s => s !== 'none')
            .map(s => {
                switch (s) {
                    case 'insomnia': return 'Insomnia';
                    case 'appetite': return 'Appetite issues';
                    case 'fatigue': return 'Easily fatigued';
                    case 'breast_tenderness': return 'Breast tenderness';
                    case 'constipation': return 'Constipation';
                    default: return s;
                }
            });
        if (symptoms.length > 0) {
            contextParts.push(`- Other symptoms: ${symptoms.join(', ')}`);
        }
    }

    // Stress
    if (answers.stress) {
        const stressMap = {
            'high': 'High stress',
            'moderate': 'Moderate stress',
            'low': 'Low stress'
        };
        contextParts.push(`- Stress level: ${stressMap[answers.stress]}`);
    }

    return contextParts.join('\n');
}

/**
 * Build AI prompt for female hormone balance questions
 */
export function buildFemalePrompt(question, answers, userName) {
    const context = buildFemaleContext(answers);
  return buildAIPrompt(SYMPTOM_NAMES.female, context, question.text, userName);
}